if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.onSlideChangedHandler = debounce(this.onSlideChanged.bind(this), 500);
        this.onThumbnailClickHandler = this.onThumbnailClick.bind(this);
        this.onVariantChangeHandler = this.handleVariantChange.bind(this);
      }

      connectedCallback() {
        if (this.elements.viewer && !this.viewerEventsBound) {
          this.viewerEventsBound = true;
          this.elements.viewer.addEventListener('slideChanged', this.onSlideChangedHandler);
        }

        if (this.elements.thumbnails && !this.thumbnailEventsBound) {
          this.thumbnailEventsBound = true;
          this.elements.thumbnails.addEventListener('click', this.onThumbnailClickHandler);
        }

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        if (this.isColorMediaFilterEnabled() && !this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChangeHandler);
          this.applyMediaFilter(this.dataset.mediaFilterSelectedValue);
        }
      }

      disconnectedCallback() {
        if (this.viewerEventsBound) {
          this.elements.viewer?.removeEventListener('slideChanged', this.onSlideChangedHandler);
          this.viewerEventsBound = false;
        }

        if (this.thumbnailEventsBound) {
          this.elements.thumbnails?.removeEventListener('click', this.onThumbnailClickHandler);
          this.thumbnailEventsBound = false;
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
          this.variantChangeUnsubscriber = null;
        }
      }

      onThumbnailClick(event) {
        const button = event.target.closest('[data-target] button');
        if (!button || !this.elements.thumbnails?.contains(button)) return;

        const mediaToSwitch = button.closest('[data-target]');
        if (!mediaToSwitch || mediaToSwitch.classList.contains('media-item--filtered-out')) return;

        this.setActiveMedia(mediaToSwitch.dataset.target, false);
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`li[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('li[data-media-id]:not(.media-item--filtered-out)');
        if (!activeMedia) {
          return;
        }

        this.elements.viewer.querySelectorAll('li[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      isColorMediaFilterEnabled() {
        return this.dataset.mediaFilterEnabled === 'true' && this.dataset.mediaFilterOptionIndex !== '';
      }

      handleVariantChange(event) {
        const data = event?.data;
        if (!this.isColorMediaFilterEnabled()) return;
        if (data?.sectionId !== this.dataset.section) return;

        const selectedValue = this.getFilterValueForVariant(data.variant);
        this.dataset.mediaFilterSelectedValue = selectedValue;
        this.applyMediaFilter(selectedValue);
      }

      getFilterValueForVariant(variant) {
        if (!variant) return '';

        const optionIndex = Number.parseInt(this.dataset.mediaFilterOptionIndex, 10);
        if (Number.isNaN(optionIndex)) return '';

        if (Array.isArray(variant.options) && typeof variant.options[optionIndex] !== 'undefined') {
          return this.normalizeFilterValue(variant.options[optionIndex]);
        }

        const legacyOptionKey = `option${optionIndex + 1}`;
        return this.normalizeFilterValue(variant[legacyOptionKey]);
      }

      normalizeFilterValue(value) {
        return `${value || ''}`.trim().toLowerCase();
      }

      getViewerMediaItems() {
        return Array.from(this.elements.viewer?.querySelectorAll('li[data-media-id]') || []);
      }

      getThumbnailItems() {
        return Array.from(this.elements.thumbnails?.querySelectorAll('li[data-target]') || []);
      }

      getModalMediaItems() {
        return Array.from(
          this.productModal?.querySelectorAll('.product-media-modal__content > [data-media-id]') || []
        );
      }

      getVisibleItems(items) {
        return items.filter((item) => !item.classList.contains('media-item--filtered-out'));
      }

      getMediaFilterValues(item) {
        const rawValues = item?.dataset?.mediaFilterValues || '';
        if (!rawValues) return [];

        return rawValues
          .split('|')
          .map((value) => this.normalizeFilterValue(value))
          .filter(Boolean);
      }

      shouldShowMediaItem(item, normalizedSelectedValue) {
        const mediaFilterValues = this.getMediaFilterValues(item);
        return mediaFilterValues.length === 0 || mediaFilterValues.includes(normalizedSelectedValue);
      }

      setItemVisibility(item, isVisible) {
        item.classList.toggle('media-item--filtered-out', !isVisible);
        if (isVisible) {
          item.removeAttribute('hidden');
          item.removeAttribute('aria-hidden');
        } else {
          item.setAttribute('hidden', '');
          item.setAttribute('aria-hidden', 'true');
        }
      }

      getActiveViewerItem() {
        return this.elements.viewer?.querySelector('li[data-media-id].is-active');
      }

      applyMediaFilter(selectedValue = '') {
        if (!this.isColorMediaFilterEnabled()) return;

        const viewerItems = this.getViewerMediaItems();
        if (!viewerItems.length) return;

        const thumbnailItems = this.getThumbnailItems();
        const modalItems = this.getModalMediaItems();
        const normalizedSelectedValue = this.normalizeFilterValue(selectedValue || this.dataset.mediaFilterSelectedValue);

        const hasMatchingTaggedItem =
          normalizedSelectedValue &&
          viewerItems.some((item) => {
            const mediaFilterValues = this.getMediaFilterValues(item);
            return mediaFilterValues.length > 0 && mediaFilterValues.includes(normalizedSelectedValue);
          });

        const showAllMedia = !normalizedSelectedValue || !hasMatchingTaggedItem;
        [viewerItems, thumbnailItems, modalItems].forEach((group) => {
          group.forEach((item) => {
            const isVisible = showAllMedia || this.shouldShowMediaItem(item, normalizedSelectedValue);
            this.setItemVisibility(item, isVisible);
          });
        });

        if (this.elements.viewer?.resetPages) this.elements.viewer.resetPages();
        if (this.elements.thumbnails?.resetPages) this.elements.thumbnails.resetPages();

        const visibleViewerItems = this.getVisibleItems(viewerItems);
        const visibleThumbnailItems = this.getVisibleItems(thumbnailItems);
        if (!visibleViewerItems.length) return;

        const activeViewerItem = this.getActiveViewerItem();
        if (!activeViewerItem || activeViewerItem.classList.contains('media-item--filtered-out')) {
          this.setActiveMedia(visibleViewerItems[0].dataset.mediaId, false);
        } else if (this.elements.thumbnails) {
          const activeThumbnail = visibleThumbnailItems.find(
            (item) => item.dataset.target === activeViewerItem.dataset.mediaId
          );
          this.setActiveThumbnail(activeThumbnail);
        }

        this.updateViewerControls(visibleViewerItems.length);
        this.updateThumbnailControls(visibleThumbnailItems.length);
        this.updateXRButtonVisibility();
      }

      updateViewerControls(visibleItemCount) {
        const sliderButtons = this.elements.viewer?.querySelector('.slider-buttons');
        if (!sliderButtons) return;

        if (visibleItemCount <= 1) {
          sliderButtons.setAttribute('hidden', '');
        } else {
          sliderButtons.removeAttribute('hidden');
        }

        if (visibleItemCount < 2) {
          const currentPageElement = sliderButtons.querySelector('.slider-counter--current');
          const totalPageElement = sliderButtons.querySelector('.slider-counter--total');
          if (currentPageElement) currentPageElement.textContent = visibleItemCount ? '1' : '0';
          if (totalPageElement) totalPageElement.textContent = `${visibleItemCount}`;
        }
      }

      updateThumbnailControls(visibleItemCount) {
        if (!this.elements.thumbnails) return;

        if (visibleItemCount <= 1) {
          this.elements.thumbnails.setAttribute('hidden', '');
        } else {
          this.elements.thumbnails.removeAttribute('hidden');
        }
      }

      updateXRButtonVisibility() {
        this.querySelectorAll('[data-shopify-model3d-id]').forEach((button) => {
          const targetMediaId = `${this.dataset.section}-${button.dataset.shopifyModel3dId}`;
          const targetMedia = this.elements.viewer?.querySelector(`li[data-media-id="${targetMediaId}"]`);
          const isVisible = targetMedia && !targetMedia.classList.contains('media-item--filtered-out');

          if (isVisible) {
            button.removeAttribute('hidden');
          } else {
            button.setAttribute('hidden', '');
          }
        });
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }
    }
  );
}
